/**
 * Price chart scale (#531): a few cents must not look like a jump. The visible
 * span covers at least ±10 % around the median price; larger real movements are
 * always shown in full. Percentages use the lowest price in the shown period as
 * their basis (decision in #531).
 */
const MIN_RELATIVE_HALF_SPAN = 0.1;
const DATA_PADDING = 1.08;
const TARGET_TICKS = 4;

export type PriceAxis = { domain: [number, number]; ticks: number[] };

function median(sorted: readonly number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function niceStep(rough: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return factor * magnitude;
}

export function priceAxis(prices: readonly number[]): PriceAxis | null {
  const values = prices.filter((p) => Number.isFinite(p) && p > 0).sort((a, b) => a - b);
  if (values.length === 0) return null;
  const low = values[0];
  const high = values[values.length - 1];
  const center = (low + high) / 2;
  const halfSpan = Math.max(((high - low) / 2) * DATA_PADDING, median(values) * MIN_RELATIVE_HALF_SPAN);
  const step = niceStep((halfSpan * 2) / TARGET_TICKS);
  const lower = Math.max(0, Math.floor((center - halfSpan) / step) * step);
  const upper = Math.ceil((center + halfSpan) / step) * step;
  const ticks: number[] = [];
  for (let tick = lower; tick <= upper + step / 2; tick += step) ticks.push(Math.round(tick * 1e6) / 1e6);
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
}

/** Percent above a basis, e.g. "+23,3 %"; the basis itself reads "±0 %". */
export function formatPercentAbove(value: number | null | undefined, basis: number | null | undefined): string | null {
  if (value === null || value === undefined || !basis || basis <= 0) return null;
  const pct = ((value - basis) / basis) * 100;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return "±0 %";
  const text = Math.abs(rounded).toLocaleString("de-AT", { maximumFractionDigits: 1 });
  return `${rounded > 0 ? "+" : "−"}${text} %`;
}
